import { isPast, parseISO } from 'date-fns'

const ALL_LETTERS = /^[a-zA-Z]*$/
const ALL_NUMBERS = /^[0-9]*$/

export default {
  methods: {
    conditionallyRequired(ifThisIsNull, label) {
      return v => {
        if (ifThisIsNull != undefined) {
          return true
        }
        if (v) {
          return true
        }
        return `${label} is required`
      }
    },
    maxLength(length) {
      return inputString => {
        if (inputString && inputString.length > length) {
          return `cannot be more than ${length} characters.`
        }
        return true
      }
    },
    minLength(length) {
      return inputString => {
        if (inputString && inputString.length < length) {
          return `cannot be less than ${length} characters.`
        }
        return true
      }
    },
    numberBetween(lowInclusive, highInclusive) {
      return inputString => {
        if (inputString) {
          const inputAsNumber = +inputString
          if (inputAsNumber < lowInclusive || inputAsNumber > highInclusive) {
            return `must be between ${lowInclusive} and ${highInclusive}.`
          }
        }
        return true
      }
    },
    isRequired(inputString) {
      if (inputString == null || inputString.length === 0) {
        return 'is required.'
      }
      return true
    },
    // Enforce a mm/YYYY or mm/YY format
    monthYear(inputString) {
      const regex = /^\d\d\/\d\d\d?\d?$/
      if (!regex.test(inputString)) {
        return 'mm/yyyy or mm/yy'
      }
      return true
    },
    isFuture(inputString) {
      if (inputString == null) {
        return true
      }
      const dateString = parseISO(inputString)
      if (isPast(dateString)) {
        return 'must be a future date'
      }
      return true
    },
    requiredOrZero(inputString) {
      if (inputString === 0) {
        return true
      }
      return this.isRequired(inputString)
    },
    forwardSlashNotAllowed(inputString) {
      if (!inputString || inputString.length === 0) {
        return true
      }

      const hasSlash = /[\\/]/g
      if (inputString.match(hasSlash)) {
        return `Cannot contain '/' character`
      }
      return true
    },
    mustBeThreeCharacters(inputString) {
      if (typeof inputString !== 'string' || inputString.trim().length !== 3) {
        return 'must be 3 characters.'
      }
      return true
    },
    mustBeFourCharacters(inputString) {
      if (!inputString || inputString.length < 4) {
        return 'must be at least 4 characters.'
      }
      return true
    },
    mustBeEightCharacters(inputString) {
      if (!inputString || inputString.length < 8) {
        return 'must be at least 8 characters.'
      }
      return true
    },
    mustMatchPassword(inputString) {
      if (inputString !== this.registerForm.password) {
        return 'ensure your passwords match.'
      }
      return true
    },
    mustMatchString(stringToMatch) {
      return inputString => {
        if (inputString !== stringToMatch) {
          return 'ensure your passwords match.'
        }
        return true
      }
    },

    mustMixLettersNumbersAndSymbols(inputString) {
      if (inputString && (inputString.match(ALL_LETTERS) || inputString.match(ALL_NUMBERS))) {
        return 'password must contain a mix of letters, numbers, or symbols.'
      }
      return true
    },
    notNegative(input) {
      if (input < 0) {
        return 'must not be a negative number'
      }
      return true
    },
    isDateStringYYYYMMDDExpectedFormat(format) {
      return input => {
        const date_string = /^\d{4}-\d{2}-\d{2}$/g
        if (!input) {
          // This does not make it required
          return false
        }

        if (input && !date_string.test(input)) {
          return `must be a date ${format}`
        }

        const dateParts = input.split('-')
        const year = Number(dateParts[0])
        const month = Number(dateParts[1])
        const date = Number(dateParts[2])
        if (year < 1800) {
          return 'year is too low'
        }
        if (month < 0 || month > 12) {
          return 'Invalid month. (yyyy-mm-dd)'
        }
        if (date < 0 || date > 31) {
          return 'Invalid day. (yyyy-mm-dd)'
        }
      }
    },
    isDateStringYYYYMMDD(input) {
      const date_string = /^\d{4}-\d{2}-\d{2}$/g
      if (!input) {
        // This does not make it required
        return false
      }

      if (input && !date_string.test(input)) {
        return 'must be a date'
      }

      const dateParts = input.split('-')
      const year = Number(dateParts[0])
      const month = Number(dateParts[1])
      const date = Number(dateParts[2])
      if (year < 1800) {
        return 'year is too low'
      }
      if (month < 0 || month > 12) {
        return 'Invalid month. (yyyy-mm-dd)'
      }
      if (date < 0 || date > 31) {
        return 'Invalid day. (yyyy-mm-dd)'
      }
    },
    wholeNumber(input) {
      const whole_number = /^[\d ]*$/g
      if (input && !whole_number.test(input)) {
        return 'must be a whole number'
      }
      return true
    },
    mustBeNumberString(input) {
      const whole_number = /^-?\d+(,\d{3})*(\.\d*)?$/g
      if (input && !whole_number.test(input)) {
        return 'must be a number'
      }
      return true
    },
    mustBeNumber(input) {
      const whole_number = /^-?[\d .]*$/g
      if (input && !whole_number.test(input)) {
        return 'must be a number'
      }
      return true
    },
    isAnEmail(inputString) {
      if (inputString && inputString.length > 0) {
        const emailPattern =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        if (!emailPattern.test(inputString)) {
          return 'must be a valid email address.'
        }
      }
      return true
    },
    isUsPhoneNumber(inputString) {
      if (inputString && inputString.length > 0) {
        const allowableChars = /^[-(). \d]{0,16}$/
        if (!allowableChars.test(inputString)) {
          return 'must be a valid US Phone Number.'
        }

        inputString = inputString.replace(/\D/g, '')
        const phoneAfterStrip = /^\d{10}$/
        if (!phoneAfterStrip.test(inputString)) {
          return 'must be a valid US Phone Number.'
        }
      }
      return true
    },
    numericLooking(inputString) {
      const regex = /^[\d,.]*$/
      if (!regex.test(inputString)) {
        return 'does not look like a number'
      }
      return true
    },
    numbersOnly(inputString) {
      if (inputString && inputString.length > 0) {
        if (!ALL_NUMBERS.test(inputString)) {
          return 'must be numbers only'
        }
      }
      return true
    },
    moneyLessThanValue(value) {
      return inputString => {
        inputString = inputString.replaceAll(/[$, .]/g, '')
        if (isNaN(inputString)) {
          return 'must be a number'
        }
        if (inputString >= value) {
          return `must be less than ${value}`
        }
        return true
      }
    },
    validTaxId() {
      // const EIN = /^[1-9]\d?-\d{7}$/
      const EIN = /^\d\d?-\d{7}$/
      return inputString => {
        if (!inputString) return true // required is a separate rule
        if (!EIN.test(inputString)) {
          return 'must be a valid taxId xx-xxxxxxx'
        }
        return true
      }
    },
    dateMMsDDsYYYY() {
      const date = /^[01]\d\/[0123]\d\/[12]\d{3}$/
      return inputString => {
        if (!date.test(inputString)) {
          return 'must be a valid date mm/dd/yyyy'
        }
        return true
      }
    },
    dateMMsDDsYYYYVal(inputString) {
      if (!inputString) return true
      const date = /^[01]\d\/[0123]\d\/[12]\d{3}$/
      if (!date.test(inputString)) {
        return 'must be a valid date mm/dd/yyyy'
      }
      return true
    },
    dateYYYYdMMdDD() {
      const date = /^[12]\d{3}-[01]\d-[0123]\d$/
      return inputString => {
        if (!inputString) return true
        if (!date.test(inputString)) {
          return 'must be a valid date yyyy-mm-dd'
        }
        return true
      }
    },
    fourDigitYear() {
      const FOUR_DIG_YEAR = /^[12]\d{3}$/
      return inputString => {
        if (inputString == null) return true
        if (!FOUR_DIG_YEAR.test(inputString)) {
          return 'must be a valid year YYYY'
        }
        return true
      }
    },
    yearOrYearRange() {
      const FOUR_DIG_YEAR = /^[12]\d{3}$/
      const FOUR_DIG_YEAR_RANGE = /^[12]\d{3}-[12]\d{3}$/
      return inputString => {
        if (FOUR_DIG_YEAR.test(inputString)) return true
        if (FOUR_DIG_YEAR_RANGE.test(inputString)) return true
        return 'must be year or year range YYYY or YYYY-YYYY'
      }
    },
    fourDigitYearVal(inputString) {
      const FOUR_DIG_YEAR = /^[12]\d{3}$/
      if (!FOUR_DIG_YEAR.test(inputString)) return 'must be a valid year YYYY'
      return true
    },
    validTfaCode(v) {
      if (v === undefined) return false
      if (!isNaN(parseInt(v)) && v.length === 6) return true
      return 'Code has to be 6 digits'
    },
    webAddress(inputString) {
      if (inputString == null || inputString === '') return true
      const FOUR_DIG_YEAR = /^(www.|http:|https:)/
      if (!FOUR_DIG_YEAR.test(inputString)) return 'must be a valid web address. (https:// or www.)'
      return true
    },
  },
}
